export const MIN_CLICKS = 4
export const RAISE_MIN_ORDERS = 3
export const RAISE_MIN_CLICKS = 10
export const MODIFIER_MIN = 0
export const MODIFIER_MAX = 900

export type BidRule = "hold" | "orders" | "next-click" | "raise-gated"

export type OrdersCpcInput = { cpc?: unknown; targetAcos?: unknown; actualAcos?: unknown }
export type NextClickCpcInput = { clicks?: unknown; aov?: unknown; targetAcos?: unknown }
export type ResolveTargetCpcInput = OrdersCpcInput &
  NextClickCpcInput & { orders?: unknown; currentBid?: unknown }
export type ResolvedTargetCpc = { cpc: number | null; rule: BidRule }
export type WeightedAdjInput = {
  spendByPlacement?: Record<string, unknown> | null
  adjPctByPlacement?: Record<string, unknown> | null
}
export type BidClamp = { minBid?: unknown; maxBid?: unknown }
export type RaiseGateInput = { orders?: unknown; clicks?: unknown }

const num = (v: unknown): number | null =>
  Number.isFinite(Number(v)) && v != null ? Number(v) : null

const pos = (v: unknown): number | null => {
  const n = num(v)
  return n != null && n > 0 ? n : null
}

const nonNeg = (v: unknown): number | null => {
  const n = num(v)
  return n != null && n >= 0 ? n : null
}

export function round2(n: unknown): number {
  return Math.round(Number(n) * 100) / 100
}

export function clamp(n: unknown, min?: unknown, max?: unknown): number {
  let v = Number(n)
  if (min != null && Number.isFinite(Number(min))) v = Math.max(Number(min), v)
  if (max != null && Number.isFinite(Number(max))) v = Math.min(Number(max), v)
  return v
}

export function hasEnoughData(clicks: unknown): boolean {
  const c = num(clicks)
  return c != null && c >= MIN_CLICKS
}

export function isEnabledTarget(state: unknown): boolean {
  return typeof state === "string" && state.toUpperCase() === "ENABLED"
}

export function targetCpcWithOrders({ cpc, targetAcos, actualAcos }: OrdersCpcInput = {}):
  | number
  | null {
  const c = Math.max(num(cpc) || 0, 0)
  const t = nonNeg(targetAcos)
  const a = pos(actualAcos)
  if (t == null || a == null) return null
  return c * (t / a)
}

export function targetCpcNextClick({ clicks, aov, targetAcos }: NextClickCpcInput = {}):
  | number
  | null {
  const p = nonNeg(aov)
  const t = nonNeg(targetAcos)
  if (p == null || t == null) return null
  const k = Math.max(num(clicks) || 0, 0)
  return (1 / (k + 1)) * p * (t / 100)
}

/** A7: true only with >= RAISE_MIN_ORDERS orders AND >= RAISE_MIN_CLICKS clicks. */
export function canRaise({ orders, clicks }: RaiseGateInput = {}): boolean {
  return (num(orders) || 0) >= RAISE_MIN_ORDERS && (num(clicks) || 0) >= RAISE_MIN_CLICKS
}

/**
 * A7 on a FINAL bid: min(newBid, currentBid) when the data cannot raise.
 * Unknown/zero currentBid or non-numeric newBid -> newBid unchanged.
 */
export function gateRaise<T>(
  newBid: T,
  currentBid: unknown,
  { orders, clicks }: RaiseGateInput = {}
): T | number {
  const n = num(newBid)
  const c = pos(currentBid)
  if (n == null || c == null || canRaise({ orders, clicks })) return newBid
  return Math.min(n, c)
}

export function resolveTargetCpc({
  cpc,
  targetAcos,
  actualAcos,
  orders,
  clicks,
  aov,
  currentBid,
}: ResolveTargetCpcInput = {}): ResolvedTargetCpc {
  if (!hasEnoughData(clicks)) return { cpc: num(currentBid), rule: "hold" }
  const resolved: ResolvedTargetCpc =
    (num(orders) || 0) > 0 && pos(actualAcos) != null
      ? { cpc: targetCpcWithOrders({ cpc, targetAcos, actualAcos }), rule: "orders" }
      : { cpc: targetCpcNextClick({ clicks, aov, targetAcos }), rule: "next-click" }
  if (resolved.cpc == null || canRaise({ orders, clicks })) return resolved
  // A7: an A2/A3 cpc above the current bid without canRaise holds the current bid.
  const current = pos(currentBid)
  if (current == null) return { cpc: null, rule: "raise-gated" }
  return resolved.cpc > current ? { cpc: current, rule: "raise-gated" } : resolved
}

export function mainModifierPct(placementCpc: unknown, baseCpc: unknown): number {
  const p = nonNeg(placementCpc)
  const b = nonNeg(baseCpc)
  if (p == null || b == null) return 0
  if (b === 0) return p > 0 ? MODIFIER_MAX : MODIFIER_MIN
  return clamp(Math.round((p / b - 1) * 100), MODIFIER_MIN, MODIFIER_MAX)
}

export function auxModifierPct(placementCpc: unknown, highestMainCpc: unknown): number {
  return mainModifierPct(placementCpc, highestMainCpc)
}

const nonNegatives = (arr: unknown): number[] =>
  Array.isArray(arr) ? arr.map(nonNeg).filter((v): v is number => v != null) : []

export function highestMain(mainCpcs: unknown): number | null {
  const xs = nonNegatives(mainCpcs)
  return xs.length ? Math.max(...xs) : null
}

export function lowestMain(mainCpcs: unknown): number | null {
  const xs = nonNegatives(mainCpcs)
  return xs.length ? Math.min(...xs) : null
}

export function weightedAdjPct({
  spendByPlacement,
  adjPctByPlacement,
}: WeightedAdjInput = {}): number {
  const spend = spendByPlacement || {}
  const adj = adjPctByPlacement || {}
  let spendSum = 0
  let weighted = 0
  for (const key of Object.keys(spend)) {
    const s = pos(spend[key])
    if (s == null) continue
    spendSum += s
    weighted += s * (num(adj[key]) || 0)
  }
  if (spendSum > 0) return weighted / spendSum
  const provided = Object.values(adj)
    .map(num)
    .filter((v): v is number => v != null)
  if (!provided.length) return 0
  return provided.reduce((a, b) => a + b, 0) / provided.length
}

export function deflateBid(
  desiredCpc: unknown,
  weightedAdjPctValue: unknown,
  { minBid, maxBid }: BidClamp = {}
): number | null {
  const d = num(desiredCpc)
  if (d == null || d < 0) return null
  const divisor = 1 + (num(weightedAdjPctValue) || 0) / 100
  if (!(divisor > 0)) return null
  return clamp(round2(d / divisor), minBid, maxBid)
}
